import { isRanked, pilotEloSeries, pilotRankAt, rankFor, rankThrough } from '@edh/shared';
import type { Pilot, Rank } from '@edh/shared';
import { Avatar } from './Avatar';
import { useStore } from '../lib/store';

/**
 * A member's picture wearing their rank.
 *
 * The rank is drawn as the border of the avatar rather than a mark beside it,
 * so it follows the person to every screen they appear on without asking for
 * any layout. At this size the ornament is the ring itself — dashed, thin,
 * solid, doubled, lit — because rays that read at 64px are noise at 24.
 *
 * Unranked members get nothing. A halo for somebody three games into placement
 * would be a claim about a rating that does not exist yet.
 */
export function RankedAvatar({
  pilot,
  size = 34,
  atMatch,
}: {
  pilot: Pilot | undefined;
  size?: number;
  /**
   * Draw the rank they were wearing when this game finished, not the one they
   * wear now.
   *
   * For anywhere a past game is being shown. Today's halo on last week's game
   * quietly rewrites it every time somebody plays — a run of wins on the way up
   * all end up drawn at the rank they arrived at, and the climb, which is the
   * only thing a history is for, disappears.
   */
  atMatch?: string;
}) {
  const { state } = useStore();
  if (!pilot) return <Avatar name="?" size={size} />;

  const plain = <Avatar name={pilot.name} src={pilot.avatar} size={size} />;

  /** Then or now, reduced to the two things the ring is drawn from. */
  const standing = ((): { rank: Rank; held: boolean; ranked: boolean } | null => {
    if (atMatch) {
      const then = pilotRankAt(state.matches, pilot.id, pilot.elo, atMatch);
      // Asked for a moment we cannot place — a game they were not in, or one
      // no longer in the log — the honest answer is no halo, not today's.
      return then && { rank: then.rank, held: then.held, ranked: then.ranked };
    }
    const now = rankThrough(pilotEloSeries(state.matches, pilot.id, pilot.elo));
    return {
      rank: rankFor(pilot.elo, now.index),
      held: now.held,
      ranked: isRanked(pilot.placementGames),
    };
  })();

  if (!standing?.ranked) return plain;
  const { rank } = standing;

  /**
   * The band is a tenth of the face, not a fixed number of pixels.
   *
   * A weight that looks right around a 62px portrait is a hoop around a 24px
   * one, and the app draws faces at both. A tenth lands on three pixels at list
   * size and six at portrait size, which is where it wanted to be either way —
   * so every other measurement here is derived from it rather than guessed
   * again per screen.
   */
  const band = Math.min(7, Math.max(2, Math.round(size / 10)));

  return (
    <span
      className={`ranked-avatar halo-${rank.halo}${rank.glow ? ' glowing' : ''}${
        standing.held ? ' held' : ''
      }`}
      style={
        {
          '--rank': rank.color,
          '--ring': `${band}px`,
          width: size,
          height: size,
        } as React.CSSProperties
      }
      title={`${rank.name} ${'I'.repeat(rank.level)}`}
    >
      {plain}
      <span className="rank-ring" aria-hidden="true" />
    </span>
  );
}
